import { MESSAGE_CHANNELS } from './messageConstants';

const SMS_GSM_SINGLE = 160;
const SMS_GSM_MULTI = 153;
const SMS_UNICODE_SINGLE = 70;
const SMS_UNICODE_MULTI = 67;

export function getField(row, ...keys) {
  for (const key of keys) {
    const value = row?.[key];
    if (value !== undefined && value !== null && String(value).trim() !== '') {
      return value;
    }
  }
  return '';
}

export function getBool(row, ...keys) {
  for (const key of keys) {
    const value = row?.[key];
    if (value === true || value === 'כן' || value === 'Y' || value === 1 || value === '1') return true;
    if (value === false || value === 'לא' || value === 'N' || value === 0 || value === '0') return false;
  }
  return false;
}

function findColumnField(columns, ...hints) {
  if (!columns?.length) return null;

  for (const hint of hints) {
    const normalizedHint = String(hint).toLowerCase();
    const match = columns.find((col) => {
      const label = String(col.label || '').toLowerCase();
      const field = String(col.field || '').toLowerCase();
      return (
        label.includes(normalizedHint)
        || field.includes(normalizedHint)
        || label === normalizedHint
        || field === normalizedHint
      );
    });

    if (match?.field) return match.field;
  }

  return null;
}

function buildDisplayLabel({ building, apartment, name }) {
  const parts = [];
  if (building) parts.push(`בניין ${building}`);
  if (apartment) parts.push(`דירה ${apartment}`);
  if (name) parts.push(name);
  return parts.join(' · ');
}

export function createTenantNormalizer(columns) {
  const fieldMap = {
    id: findColumnField(columns, 'docentry', 'tenantid', 'מזהה', 'id'),
    name: findColumnField(columns, 'שם', 'name', 'tenant'),
    apartment: findColumnField(columns, 'דירה', 'apartment'),
    building: findColumnField(columns, 'בניין', 'building'),
    appType: findColumnField(columns, 'טיפוס', 'apptype'),
    family: findColumnField(columns, 'משפחה', 'family'),
    phone: findColumnField(columns, 'טלפון 1', 'טלפון', 'phone'),
    email: findColumnField(columns, 'מייל', 'email'),
    city: findColumnField(columns, 'עיר', 'city'),
    alwEmail: findColumnField(columns, 'מורשה שליחה במייל', 'alwemail'),
    alwSms: findColumnField(columns, 'מורשה שליחת הודעה', 'alwsms', 'smsapproval'),
    alwWapp: findColumnField(columns, 'מורשה שליחה בוואצאפ', 'alwwapp'),
  };

  return (row, index) => {
    const pick = (mappedKey, ...fallbackKeys) => {
      const mappedField = fieldMap[mappedKey];
      if (mappedField && row[mappedField] !== undefined && row[mappedField] !== null) {
        return row[mappedField];
      }
      return getField(row, ...fallbackKeys);
    };

    const pickBool = (mappedKey, ...fallbackKeys) => {
      const mappedField = fieldMap[mappedKey];
      if (mappedField && row[mappedField] !== undefined) {
        return getBool(row, mappedField);
      }
      return getBool(row, ...fallbackKeys);
    };

    const tenant = {
      id: pick('id', 'id', 'U_Id', 'tenantId', 'DocEntry') || `tenant-${index}`,
      name: pick('name', 'name', 'U_Name', 'tenantName', 'שם'),
      apartment: pick('apartment', 'apartment', 'U_Apartment', 'דירה'),
      building: pick('building', 'building', 'U_Building', 'בניין'),
      appType: pick('appType', 'appType', 'U_AppType', 'טיפוס'),
      family: pick('family', 'family', 'U_Family', 'משפחה'),
      phone: pick('phone', 'phone1', 'phone', 'U_Phone', 'טלפון', 'טלפון 1'),
      email: pick('email', 'email', 'U_Email', 'מייל'),
      city: pick('city', 'city', 'U_City', 'עיר'),
      AlwEmail: pickBool('alwEmail', 'AlwEmail', 'U_AlwEmail', 'מורשה שליחה במייל'),
      U_AlwSms: pickBool('alwSms', 'U_AlwSms', 'AlwSms', 'SmsApproval', 'מורשה שליחת הודעה'),
      U_AlwWApp: pickBool('alwWapp', 'U_AlwWApp', 'AlwWApp', 'מורשה שליחה בוואצאפ'),
      _raw: row,
    };

    tenant.displayLabel = buildDisplayLabel(tenant) || `דייר ${index + 1}`;
    return tenant;
  };
}

export function normalizeTenant(row, index = 0) {
  return createTenantNormalizer([])(row, index);
}

export function isUnicodeSms(text) {
  return /[^\x00-\x7F]/.test(text || '');
}

export function getSmsParts(text) {
  const content = text || '';
  const chars = content.length;

  if (chars === 0) {
    return { chars: 0, parts: 0, isUnicode: false, limit: SMS_GSM_SINGLE, costLabel: '0 SMS' };
  }

  const isUnicode = isUnicodeSms(content);
  const singleLimit = isUnicode ? SMS_UNICODE_SINGLE : SMS_GSM_SINGLE;
  const multiLimit = isUnicode ? SMS_UNICODE_MULTI : SMS_GSM_MULTI;

  if (chars <= singleLimit) {
    return {
      chars,
      parts: 1,
      isUnicode,
      limit: singleLimit,
      costLabel: '1 SMS',
    };
  }

  const parts = Math.ceil(chars / multiLimit);
  return {
    chars,
    parts,
    isUnicode,
    limit: singleLimit,
    costLabel: `${parts} SMS (עלות ×${parts})`,
  };
}

export function hasConsent(tenant, channelKey) {
  const channel = MESSAGE_CHANNELS[channelKey];
  if (!channel) return false;

  const field = channel.consentField;
  return Boolean(tenant[field]);
}

export function hasContact(tenant, channelKey) {
  if (channelKey === 'email') return Boolean(tenant.email);
  if (channelKey === 'sms' || channelKey === 'whatsapp') return Boolean(tenant.phone);
  return false;
}

export function canSendToTenant(tenant, channelKey) {
  return hasConsent(tenant, channelKey) && hasContact(tenant, channelKey);
}

export function getEligibleChannelsForTenant(tenant, channels) {
  return Object.keys(MESSAGE_CHANNELS).filter(
    (key) => channels[key] && canSendToTenant(tenant, key)
  );
}

export function buildSendSummary(selectedTenants, channels, messageBody) {
  const smsInfo = getSmsParts(messageBody);
  const activeChannels = Object.keys(MESSAGE_CHANNELS).filter((key) => channels[key]);

  const eligibleTenants = selectedTenants.filter((tenant) =>
    getEligibleChannelsForTenant(tenant, channels).length > 0
  );

  const byChannel = {
    email: 0,
    sms: 0,
    whatsapp: 0,
  };

  const blockedReasons = {
    noConsent: 0,
    noContact: 0,
    noChannel: 0,
  };

  selectedTenants.forEach((tenant) => {
    let canSendAny = false;

    activeChannels.forEach((channelKey) => {
      if (!channels[channelKey]) return;

      if (!hasConsent(tenant, channelKey)) return;
      if (!hasContact(tenant, channelKey)) return;

      byChannel[channelKey] += 1;
      canSendAny = true;
    });

    if (!canSendAny) {
      const hasAnyConsent = activeChannels.some((key) => hasConsent(tenant, key));
      const hasAnyContact = activeChannels.some((key) => hasContact(tenant, key));

      if (!hasAnyConsent) blockedReasons.noConsent += 1;
      else if (!hasAnyContact) blockedReasons.noContact += 1;
      else blockedReasons.noChannel += 1;
    }
  });

  const totalSmsMessages = channels.sms ? byChannel.sms * smsInfo.parts : 0;

  return {
    totalSelected: selectedTenants.length,
    willSend: eligibleTenants.length,
    blocked: selectedTenants.length - eligibleTenants.length,
    byChannel,
    blockedReasons,
    smsInfo,
    totalSmsMessages,
    activeChannels,
  };
}

export function fillTemplate(text, tenant, context = {}) {
  if (!text) return '';

  const options = typeof context === 'string'
    ? { projectName: context }
    : context;

  const replacements = {
    '{{שם_דייר}}': tenant.name || '',
    '{{משפחה}}': tenant.family || '',
    '{{מספר_דירה}}': tenant.apartment || '',
    '{{בניין}}': tenant.building || '',
    '{{טיפוס}}': tenant.appType || '',
    '{{שם_קבלן}}': options.contractorName || '',
    '{{שם_פרויקט}}': options.projectName || '',
    '{{תאריך}}': new Date().toLocaleDateString('he-IL'),
    '{{שעה}}': '',
    '{{עיר}}': tenant.city || '',
  };

  return Object.entries(replacements).reduce(
    (result, [token, value]) => result.split(token).join(value),
    text
  );
}

export function isMessagingUser(userType) {
  return userType === 'U' || userType === 'SU';
}

export function isTenantUser(userType) {
  if (!userType) return false;
  const normalized = String(userType).trim().toUpperCase();
  return normalized === 'T' || normalized === 'TN' || normalized === 'TENANT';
}

export function normalizePhoneForWhatsApp(phone) {
  if (!phone) return null;

  let digits = String(phone).replace(/\D/g, '');
  if (!digits) return null;

  if (digits.startsWith('972')) return digits;
  if (digits.startsWith('0')) return `972${digits.slice(1)}`;
  if (digits.length === 9 && digits.startsWith('5')) return `972${digits}`;

  return digits.length >= 9 ? digits : null;
}

export function buildWhatsAppMessage(subject, body, tenant, context = {}) {
  const filledSubject = fillTemplate(subject, tenant, context);
  const filledBody = fillTemplate(body, tenant, context);

  if (filledSubject && filledBody) {
    return `${filledSubject}\n\n${filledBody}`;
  }

  return (filledSubject || filledBody || '').trim();
}

export function buildWhatsAppUrl(phone, message) {
  const normalizedPhone = normalizePhoneForWhatsApp(phone);
  const text = (message || '').trim();

  if (!normalizedPhone || !text) return null;

  return `https://wa.me/${normalizedPhone}?text=${encodeURIComponent(text)}`;
}
